import { useState } from "react";
import { clsx } from "clsx";

const SUPPLIED_MATERIALS = [
  { icon: "ri-book-open-line", label: "핵심 문법 정리" },
  { icon: "ri-refresh-line", label: "복습 자료" },
  { icon: "ri-file-list-3-line", label: "말하기, 단어 숙제" },
  { icon: "ri-translate", label: "추가 단어 및 표현 정리" },
];

export default function Materials() {
  const [imgLoaded, setImgLoaded] = useState(false);

  return (
    <section className="py-10 md:py-28 bg-background-50">
      <div className="max-w-6xl mx-auto px-4 md:px-10">
        <div className="text-center mb-14">
          <span className="text-[10px] md:text-sm font-semibold tracking-widest text-accent-700 uppercase">
            Textbook &amp; Materials
          </span>
          <h2 className="mt-3 font-heading text-xl md:text-4xl font-bold text-foreground-950">
            사용 교재 및 학습 자료
          </h2>
          <p className="mt-2 text-xs sm:text-base text-foreground-700">
            수업은 검증된 시중 교재를 중심으로 진행합니다.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-10 items-stretch">
          {/* 교재 소개 카드 */}
          <div className="bg-background-100 border border-background-200 rounded-2xl px-3 md:px-8 pt-5 md:pt-8 pb-3 md:pb-4 flex flex-col h-full">
            <div className="flex flex-col sm:flex-row gap-6 items-center sm:items-start flex-1">
              {/* 교재 표지 이미지 */}
              <div
                className={clsx(
                  "shrink-0 w-28 h-40 sm:w-40 sm:h-56 rounded-xl border border-background-200 overflow-hidden bg-background-50 flex items-center justify-center",
                  !imgLoaded && "animate-pulse"
                )}
              >
                <img
                  src="https://static.readdy.ai/image/52caad4467cefba3c8ee9c3d475c44ad/5924d81a126c70fb7807ab6d0af42d76.jpeg"
                  alt="시사일본어사 착 붙는 일본어 독학 첫걸음 표지"
                  className={clsx(
                    "w-full h-full object-cover object-top transition-opacity duration-500",
                    imgLoaded ? "opacity-100" : "opacity-0"
                  )}
                  onLoad={() => setImgLoaded(true)}
                />
              </div>

              <div className="text-center sm:text-left">
                <span className="inline-block px-2.5 py-1 rounded bg-primary-100 text-primary-700 text-[11px] font-semibold tracking-wide mb-3">
                  정규 교재
                </span>
                <h3 className="font-heading text-base md:text-xl font-bold text-foreground-950 leading-snug">
                  『착! 붙는 일본어 독학 첫걸음』
                </h3>
                <p className="mt-1.5 text-xs sm:text-sm text-foreground-600">
                  시사일본어사 출간
                </p>
                <p className="mt-4 text-xs sm:text-sm text-foreground-700 leading-relaxed">
                  JLPT N5~N4 범위의 기초 문법과 표현을 체계적으로 다루는 베스트셀러 입문 교재입니다. 수업에서 본 교재를 중심으로 해설과 연습을 함께 진행합니다.
                </p>
              </div>
            </div>

            <a
              href="http://product.kyobobook.co.kr/detail/S000212686783"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="mt-3 flex flex-wrap sm:flex-nowrap items-center justify-center gap-2 bg-background-100 border border-background-300 text-foreground-700 rounded-xl py-3 sm:py-3.5 px-3 sm:px-5 text-center hover:bg-background-200 transition-colors"
            >
              <span className="text-sm md:text-base font-semibold whitespace-nowrap">
                교재 구매 바로가기
              </span>
              <span className="text-xs md:text-sm text-foreground-500 whitespace-nowrap">
                (교보문고)
              </span>
              <i className="ri-arrow-right-line text-base md:text-lg text-foreground-400" />
            </a>

            <div className="mt-3 pt-3 border-t border-background-200">
              <div className="flex items-center gap-4">
                <div className="w-9 h-9 sm:w-11 sm:h-11 shrink-0 rounded-xl overflow-hidden flex items-center justify-center">
                  <img
                    src="https://static.readdy.ai/image/52caad4467cefba3c8ee9c3d475c44ad/0029587700768d2941e0d3fd5424e85d.png"
                    alt="클래스카드 로고"
                    className="w-full h-full object-contain"
                  />
                </div>
                <div>
                  <h4 className="text-xs sm:text-sm font-semibold text-foreground-900">
                    클래스카드 단어 학습 프로그램
                  </h4>
                  <p className="text-xs sm:text-sm text-foreground-600 leading-relaxed">
                    단어 학습 프로그램을 통해 체계적인 어휘 학습 관리를 제공합니다
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* 수업 제공 자료 카드 */}
          <div className="bg-background-100 border border-background-200 rounded-2xl px-3 md:px-8 pt-5 md:pt-8 pb-3 md:pb-4 h-full flex flex-col">
            <span className="inline-block px-2.5 py-1 rounded bg-accent-100 text-accent-700 text-[11px] font-semibold tracking-wide mb-3">
              수업 제공 자료
            </span>
            <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-4">
              매 수업마다 함께 드려요
            </h3>

            <ul className="space-y-3 flex-1">
              {SUPPLIED_MATERIALS.map((item) => (
                <li
                  key={item.label}
                  className="flex items-center gap-3 bg-background-50 rounded-xl px-3 py-3 sm:px-4 sm:py-3.5 border border-background-200"
                >
                  <div className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 shrink-0">
                    <i className={clsx(item.icon, "text-base sm:text-lg")} />
                  </div>
                  <span className="text-sm md:text-base font-medium text-foreground-900">
                    {item.label}
                  </span>
                </li>
              ))}
            </ul>

            <p className="mt-4 text-xs text-foreground-500 leading-relaxed">
              * 학습 자료는 시중 교재를 대체하는 것이 아니라, 수업의 이해와 복습을 돕기 위한 보조 자료입니다.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}